import { c as _c } from "react/compiler-runtime";
import { LazyImage, type LazyImagePropertiesType } from "@website/ui/lazy-image";
import { Skeleton } from "@website/ui/skeleton";
import { FC, useEffect, useRef, useState } from "react";
const IMAGE_LOAD_ROOT_MARGIN = "300px 0px";
type AccommodationCardImagePropertiesType = LazyImagePropertiesType & {
  deferUntilVisible?: boolean;
};
const useRenderWhenVisible = deferUntilVisible => {
  const $ = _c(6);
  const containerReference = useRef(null);
  const [shouldRender, setShouldRender] = useState(!deferUntilVisible);
  let t0;
  let t1;
  if ($[0] !== deferUntilVisible || $[1] !== shouldRender) {
    t0 = () => {
      if (shouldRender) {
        return;
      }
      if (!deferUntilVisible || typeof IntersectionObserver === "undefined") {
        setShouldRender(true);
        return;
      }
      const container = containerReference.current;
      if (!container) {
        return;
      }
      const observer = new IntersectionObserver(t2 => {
        const [entry] = t2;
        if (!entry.isIntersecting) {
          return;
        }
        setShouldRender(true);
        observer.disconnect();
      }, {
        rootMargin: IMAGE_LOAD_ROOT_MARGIN
      });
      observer.observe(container);
      return () => observer.disconnect();
    };
    t1 = [deferUntilVisible, shouldRender];
    $[0] = deferUntilVisible;
    $[1] = shouldRender;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== shouldRender) {
    t2 = {
      containerReference,
      shouldRender
    };
    $[4] = shouldRender;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const AccommodationCardImage: FC<AccommodationCardImagePropertiesType> = t0 => {
  const $ = _c(16);
  let fallback;
  let properties;
  let src;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className: t1,
      deferUntilVisible: t2,
      fallback,
      src,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = fallback;
    $[2] = properties;
    $[3] = src;
    $[4] = t1;
    $[5] = t2;
  } else {
    fallback = $[1];
    properties = $[2];
    src = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const className = t1 === undefined ? "" : t1;
  const deferUntilVisible = t2 === undefined ? false : t2;
  const {
    containerReference,
    shouldRender
  } = useRenderWhenVisible(deferUntilVisible);
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0"><Skeleton className="!rounded-[14px]" /></div>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== className || $[8] !== fallback || $[9] !== properties || $[10] !== shouldRender || $[11] !== src) {
    t4 = shouldRender && <LazyImage {...properties} className={`${className} relative z-10`} fallback={fallback} src={src} />;
    $[7] = className;
    $[8] = fallback;
    $[9] = properties;
    $[10] = shouldRender;
    $[11] = src;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== containerReference || $[14] !== t4) {
    t5 = <div className="relative size-full" ref={containerReference}>{t3}{t4}</div>;
    $[13] = containerReference;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};